import { api } from './api';
import { documentStatusService } from './documentStatusService';

export interface VectorSearchRequest {
  query: string;
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  similarityThreshold?: number;
}

// Interface para o chunk de documento retornado pela API
export interface VectorSearchChunk {
  documentId?: string;
  fileName?: string;
  content?: string;
  metadata?: Record<string, any>;
  relevanceScore?: number;
}

// Interface para o resultado individual da busca vetorial
export interface VectorSearchResultItem {
  confidence: number;
  documentId: string;
  metadata?: {
    Size?: string;
    Tags?: string[];
    Author?: string;
    Resume?: string;
    Status?: string;
    ContentType?: string;
    CreatedAt?: string;
    FileName?: string;
    UpdatedAt?: string;
    Updater?: string;
    Categories?: string[];
  };
  sourceChunks?: VectorSearchChunk[];
  sourceDocuments?: VectorSearchChunk[];
}

// Interface para o resultado completo da busca vetorial
export interface VectorSearchApiResponse {
  results: VectorSearchResultItem[];
  totalResults: number;
  searchTime: number;
  query: string;
}

// Interface para compatibilidade com o sistema existente
export interface VectorSearchDocument {
  id: string;
  title: string;
  content: string;
  description?: string;
  metadata?: Record<string, any>;
  similarityScore: number;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface VectorSearchResult {
  documents: VectorSearchDocument[];
  totalResults: number;
  page: number;
  pageSize: number;
  totalPages: number;
  searchTime: number;
  query: string;
}

export interface DocumentIngestionRequest {
  documentId: string;
  title: string;
  content: string;
  description?: string;
  metadata?: Record<string, any>;
  tags?: string[];
  categories?: string[];
}

export interface DocumentIngestionResponse {
  success: boolean;
  documentId: string;
  message: string;
  vectorId?: string;
}

export interface DocumentRemovalRequest {
  documentId: string;
}

export interface DocumentRemovalResponse {
  success: boolean;
  message: string;
}

class VectorSearchService {
  /**
   * Realiza busca vetorial no banco de dados vetorial
   */
  async vectorSearch(request: VectorSearchRequest): Promise<VectorSearchResult> {
    try {
      const params = new URLSearchParams({
        query: request.query,
        page: (request.page || 1).toString(),
        pageSize: (request.pageSize || 10).toString(),
        similarityThreshold: (request.similarityThreshold || 0.7).toString()
      });

      const response = await api.get(`/search/vector?${params}`);
      
      // Processar a resposta da API
      const results = response.data;
      
      let documents: VectorSearchDocument[] = [];
      
      if (Array.isArray(results)) {
        // A API retornou um array diretamente
        documents = results.map((doc: any, index: number) => ({
          id: doc.id || `doc_${index}`,
          title: doc.title || doc.document_title || 'Documento sem título',
          content: doc.content || doc.extracted_content || '',
          description: doc.description || doc.metadata?.Description || '',
          path: doc.path || doc.metadata?.Path || `/documents/${doc.id}`,
          metadata: doc.metadata || {},
          similarityScore: doc.similarity_score || doc.score || 0.0,
          createdAt: doc.created_at || doc.createdAt || new Date().toISOString(),
          updatedAt: doc.updated_at || doc.updatedAt || new Date().toISOString()
        }));
      } else if (results?.documents) {
        // A API retornou uma estrutura com propriedade documents
        documents = results.documents.map((doc: any, index: number) => ({
          id: doc.id || `doc_${index}`,
          title: doc.title || doc.document_title || 'Documento sem título',
          content: doc.content || doc.extracted_content || '',
          description: doc.description || doc.metadata?.Description || '',
          path: doc.path || doc.metadata?.Path || `/documents/${doc.id}`,
          metadata: doc.metadata || {},
          similarityScore: doc.similarity_score || doc.score || 0.0,
          createdAt: doc.created_at || doc.createdAt || new Date().toISOString(),
          updatedAt: doc.updated_at || doc.updatedAt || new Date().toISOString()
        }));
      }

      return {
        documents,
        totalResults: documents.length,
        page: request.page || 1,
        pageSize: request.pageSize || 10,
        totalPages: Math.ceil(documents.length / (request.pageSize || 10)),
        searchTime: 0,
        query: request.query
      };
      
    } catch (error: any) {
      console.error('❌ Erro na busca vetorial:', error);
      throw new Error(`Erro na busca vetorial: ${error.message || 'Erro desconhecido'}`);
    }
  }

  /**
   * Realiza busca híbrida combinando busca vetorial com filtros
   * Nota: Endpoint híbrido não disponível na API atual
   */
  async hybridSearch(request: VectorSearchRequest): Promise<VectorSearchResult> {
    try {
      // Como não há endpoint híbrido, usar busca vetorial pura
      console.warn('⚠️ Endpoint híbrido não disponível, usando busca vetorial pura');
      return await this.vectorSearch(request);
    } catch (error) {
      console.error('❌ Erro na busca híbrida:', error);
      throw error;
    }
  }

  /**
   * Ingesta um documento no banco vetorial
   */
  async ingestDocument(request: DocumentIngestionRequest): Promise<DocumentIngestionResponse> {
    try {
      const response = await api.post('/index/vector', request);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro na ingestão do documento:', error);
      throw error;
    }
  }

  /**
   * Remove um documento do banco vetorial
   */
  async removeDocument(request: DocumentRemovalRequest): Promise<DocumentRemovalResponse> {
    try {
      const response = await api.delete('/index/vector', { data: request });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro na remoção do documento:', error);
      throw error;
    }
  }

  /**
   * Verifica se um documento está indexado no banco vetorial
   */
  async isDocumentIndexed(documentId: string): Promise<boolean> {
    try {
      const status = await documentStatusService.isDocumentIndexedForChatbot(documentId);
      return status;
    } catch (error: any) {
      console.error('❌ Erro ao verificar status do documento:', error);
      return false;
    }
  }

  /**
   * Atualiza um documento já indexado
   */
  async updateDocument(request: DocumentIngestionRequest): Promise<DocumentIngestionResponse> {
    try {
      const response = await api.put('/index/vector', request);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro na atualização do documento:', error);
      throw error;
    }
  }

  /**
   * Obtém estatísticas do banco vetorial
   */
  async getStats(): Promise<any> {
    try {
      const response = await api.get('/index/vector/stats');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao obter estatísticas:', error);
      throw error;
    }
  }

  /**
   * Realiza busca por similaridade usando o novo endpoint otimizado para chatbot
   */
  async similaritySearchForChatbot(query: string, maxResults: number = 5): Promise<VectorSearchDocument[]> {
    try {
      const response = await api.post('/index/vectorsearch/similarity', {
        query,
        maxResults
      });

      const results = response.data;
      
      if (!results || !Array.isArray(results)) {
        console.warn('⚠️ Resposta da API não contém resultados válidos:', results);
        return [];
      }

      // Converter os resultados para o formato esperado
      const documents: VectorSearchDocument[] = results.map((doc: any, index: number) => ({
        id: doc.id || doc.documentId || `doc_${index}`,
        title: doc.title || doc.documentTitle || doc.fileName || 'Documento sem título',
        content: doc.content || doc.extractedContent || doc.text || '',
        description: doc.description || doc.summary || doc.metadata?.Description || '',
        metadata: doc.metadata || {},
        similarityScore: doc.similarityScore || doc.score || doc.confidence || 0.0,
        source: doc.source || 'similarity_search',
        createdAt: doc.createdAt || doc.created_at || new Date().toISOString(),
        updatedAt: doc.updatedAt || doc.updated_at || new Date().toISOString()
      }));

      // Ordenar por score de similaridade (maior primeiro)
      documents.sort((a, b) => b.similarityScore - a.similarityScore);

      return documents;
    } catch (error: any) {
      console.error('❌ Erro na busca por similaridade para chatbot:', error);
      throw new Error(`Erro na busca por similaridade: ${error.message || 'Erro desconhecido'}`);
    }
  }

  /**
   * Realiza busca semântica otimizada para o chatbot
   */
  async semanticSearchForChatbot(query: string, maxResults: number = 5): Promise<VectorSearchDocument[]> {
    try {
      // Usar o novo endpoint de similaridade se disponível
      return await this.similaritySearchForChatbot(query, maxResults);
    } catch (error) {
      console.warn('⚠️ Endpoint de similaridade não disponível, usando busca vetorial padrão:', error);
      
      // Fallback para o método anterior
      const result = await this.vectorSearch({
        query,
        page: 1,
        pageSize: maxResults || 5,
        similarityThreshold: 0.6 // Threshold mais baixo para o chatbot
      });

      return result.documents || [];
    }
  }

  /**
   * Converte documentos do banco vetorial para o formato esperado pelo sistema
   */
  convertToSearchDocument(vectorDoc: VectorSearchDocument): any {
    return {
      id: vectorDoc.id,
      customerId: 'default',
      title: vectorDoc.title,
      description: vectorDoc.description || vectorDoc.content?.substring(0, 200) + '...' || '',
      path: `/documents/${vectorDoc.id}`,
      nodeType: 'document',
      status: 'published',
      indexedForSearch: true,
      createdAt: vectorDoc.createdAt || new Date().toISOString(),
      updatedAt: vectorDoc.updatedAt || new Date().toISOString(),
      score: vectorDoc.similarityScore,
      metadata: vectorDoc.metadata || {}
    };
  }
}

export const vectorSearchService = new VectorSearchService();
